import React, { FC, useState, useEffect, useMemo } from 'react';
import type { PopoverProps } from '@wix/design-system/dist/types/Popover';
import { Box, Popover, TextButton, TextButtonWeight } from '@wix/design-system';
import { ChevronDown, ChevronUp, Languages } from '@wix/wix-ui-icons-common';
import { marketplaceHeaderClientGenericClick } from '@wix/bi-logger-marketplace/v2';
import { dataHooks, popoverIds } from '../utils/constants';
import { useLanguageSelected } from '../../Hooks/useLanguageSelected';
import { useBi } from '../../Hooks/useBi';
import {
  LanguageOption,
  getTranslatedLanguageOptions,
  getLanguageSelectedHref,
  getLanguageLabel,
} from '../utils/Language/formatLanguageOptions';
import s from './LanguageSelector.module.scss';

interface LanguageSelectorTypeProps {
  placement: PopoverProps['placement'];
  moveBy: { x: number; y: number };
  moveByWhenCurrency: { x: number; y: number };
  moveByWhenTemplates: { x: number; y: number };
  showArrow: boolean;
  className: string;
  triggerElementWeight: TextButtonWeight;
  enableClickTrigger: boolean;
  enableHoverTrigger: boolean;
}
export interface LanguageSelectorProps {
  value?: string;
  pageName: string;
  isLess1000View?: boolean;
  type?: 'loggedIn' | 'notLoggedIn';
  enableMarketplaceTemplates?: boolean;
  forceShowLanguageLabel?: boolean;
  showSuffixIcon?: boolean;
  hidePrefixIcon?: boolean;
}

const defaultNotLoggedInProps: LanguageSelectorTypeProps = {
  placement: 'bottom',
  moveBy: { x: 0, y: 20 },
  moveByWhenTemplates: { x: 0, y: 20 },
  moveByWhenCurrency: { x: 0, y: 20 },
  showArrow: true,
  className: '',
  triggerElementWeight: 'normal',
  enableClickTrigger: true,
  enableHoverTrigger: false,
};

const defaultLoggedInProps: LanguageSelectorTypeProps = {
  placement: 'left',
  moveBy: { x: 0, y: 76 },
  moveByWhenTemplates: { x: 0, y: 112 },
  moveByWhenCurrency: { x: 0, y: 76 },
  showArrow: false,
  className: 'menuLink',
  triggerElementWeight: 'thin',
  enableClickTrigger: false,
  enableHoverTrigger: true,
};

const LanguageSelector: FC<LanguageSelectorProps> = ({
  value = 'en',
  pageName,
  isLess1000View = false,
  type = 'notLoggedIn',
  enableMarketplaceTemplates = false,
  forceShowLanguageLabel = false,
  showSuffixIcon = false,
  hidePrefixIcon = false,
}) => {
  const onLanguageSelected = useLanguageSelected(pageName, value);
  const logger = useBi();
  const [shown, setShown] = useState<boolean>(false);
  const options = getTranslatedLanguageOptions({ desktpo: true });
  const popoverProps =
    type === 'loggedIn' ? defaultLoggedInProps : defaultNotLoggedInProps;

  const SuffixIcon = !showSuffixIcon ? undefined : shown ? (
    <ChevronUp />
  ) : (
    <ChevronDown />
  );
  const PrefixIcon = hidePrefixIcon ? undefined : <Languages />;

  useEffect(() => {
    if (isLess1000View) {
      setShown(false);
    }
  }, [isLess1000View]);

  const OptionCell = ({
    index,
    option,
    selected,
  }: {
    index: number;
    option: LanguageOption;
    selected?: boolean;
  }) => (
    <Box
      className={`${s.optionLinkContainer} ${
        selected ? s.optionLinkContainerActive : ''
      } ${
        index % 2 === 0 ? s.optionLinkContainerEven : s.optionLinkContainerOdd
      }`}
    >
      <TextButton
        as="a"
        skin="dark"
        dataHook={`language-popover-item-${option.id}`}
        href={getLanguageSelectedHref(option)}
        onClick={(e: any) => {
          e.preventDefault();
          onLanguageSelected(option);
        }}
        className={`${s.optionLink} ${s.languageLink} ${
          selected ? s.optionLinkActive : ''
        }`}
      >
        {option.label}
      </TextButton>
    </Box>
  );

  const getMoveBy = useMemo(
    () =>
      enableMarketplaceTemplates
        ? popoverProps.moveByWhenTemplates
        : pageName === 'marketplace home' || pageName === ''
        ? popoverProps.moveBy
        : popoverProps.moveByWhenCurrency,
    [pageName],
  );

  return (
    <Box
      dataHook={dataHooks.languageSelector}
      className={s.languageSelectorBox}
      display="block"
      width="100%"
    >
      <Popover
        shown={shown}
        showArrow={popoverProps.showArrow}
        appendTo="parent"
        placement={popoverProps.placement}
        zIndex={5000}
        dataHook="language-popover-menu"
        fixed={false}
        minWidth={280}
        moveBy={getMoveBy}
        onClickOutside={() => setShown(false)}
        disableClickOutsideWhenClosed={true}
        id={popoverIds.languagePopover}
        onKeyDown={(e: any) => {
          if (e.keyCode === 13) {
            setShown(!shown);
          }
        }}
        onClick={() => {
          if (popoverProps.enableClickTrigger) {
            logger.report(
              marketplaceHeaderClientGenericClick({
                clickType: `${
                  !shown ? 'open' : 'close'
                } language picker drop down`,
                pageName,
              }),
            );
            setShown(!shown);
          }
        }}
        onMouseEnter={() => {
          if (popoverProps.enableHoverTrigger) {
            setShown(true);
          }
        }}
        onMouseLeave={() => {
          if (popoverProps.enableHoverTrigger) {
            setShown(false);
          }
        }}
      >
        <Popover.Element>
          <Box display="block" width="100%">
            <TextButton
              skin="dark"
              size="medium"
              weight={popoverProps.triggerElementWeight}
              prefixIcon={PrefixIcon}
              suffixIcon={SuffixIcon}
              dataHook="language-popover-trigger"
              className={
                popoverProps.className ? s[popoverProps.className] : ''
              }
            >
              {isLess1000View || type === 'loggedIn' || forceShowLanguageLabel
                ? getLanguageLabel(value)
                : ''}
            </TextButton>
          </Box>
        </Popover.Element>
        <Popover.Content>
          <div
            style={{ padding: '30px 0 32px' }}
            onKeyDown={(e: any) => {
              if (e.keyCode === 27) {
                e.stopPropagation();
                setShown(false);
                (
                  document.querySelector(
                    `#${popoverIds.languagePopover} button`,
                  ) as HTMLElement
                )?.focus();
              }
            }}
          >
            <Box
              className={s.languageSelector}
              gap="SP0"
              width="100%"
              display="block"
            >
              {options?.map((option, index) => (
                <OptionCell
                  index={index}
                  option={option}
                  key={option.id}
                  selected={option.id === value}
                />
              ))}
            </Box>
          </div>
        </Popover.Content>
      </Popover>
    </Box>
  );
};

export default LanguageSelector;
